// Simplified Google Drive service - no user configuration needed
export interface SimplifiedGoogleDriveConfig {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}

class SimplifiedGoogleDriveService {
  private config: SimplifiedGoogleDriveConfig | null = null;
  private readonly storageKey = 'godmode_drive_tokens_v2';
  private readonly encryptionKey = 'godmode_drive_key';

  constructor() {
    this.loadConfig();
  }

  private async generateEncryptionKey(): Promise<CryptoKey> {
    // Get or create a persistent key for encrypting tokens
    let keyData = localStorage.getItem(this.encryptionKey);

    if (!keyData) {
      // Generate a new key
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      const exported = await crypto.subtle.exportKey('raw', key);
      keyData = btoa(String.fromCharCode(...new Uint8Array(exported)));
      localStorage.setItem(this.encryptionKey, keyData);
    }

    // Import the key
    const keyBuffer = Uint8Array.from(atob(keyData), c => c.charCodeAt(0));
    return crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private async encryptData(data: string): Promise<string> {
    try {
      const key = await this.generateEncryptionKey();
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encoded = new TextEncoder().encode(data);

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoded
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption failed, falling back to base64:', error);
      // Fallback to simple base64 if Web Crypto API fails
      return btoa(data);
    }
  }

  private async decryptData(encryptedData: string): Promise<string> {
    try {
      const key = await this.generateEncryptionKey();
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

      // Extract IV and encrypted content
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Decryption failed, trying base64 fallback:', error);
      // Try simple base64 decoding as fallback
      try {
        return atob(encryptedData);
      } catch {
        throw new Error('Failed to decrypt drive tokens');
      }
    }
  }

  private async loadConfig(): Promise<void> {
    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      try {
        const decrypted = await this.decryptData(stored);
        const parsed = JSON.parse(decrypted);
        this.config = {
          ...parsed,
          expiresAt: new Date(parsed.expiresAt)
        };
      } catch (error) {
        console.error('Failed to load Drive config:', error);
        // Clear corrupted data
        localStorage.removeItem(this.storageKey);
      }
    }
  }

  async saveTokensFromUrl(encodedTokens: string): Promise<void> {
    try {
      // Decode tokens from URL (base64 encoded from server)
      const tokenData = JSON.parse(atob(encodedTokens));

      this.config = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: new Date(Date.now() + tokenData.expires_in * 1000)
      };

      // Encrypt and store
      const encrypted = await this.encryptData(JSON.stringify(this.config));
      localStorage.setItem(this.storageKey, encrypted);

      console.log('✅ Google Drive connected successfully');
    } catch (error) {
      console.error('Failed to save Drive tokens:', error);
      throw new Error('Failed to save Google Drive configuration');
    }
  }

  isConfigured(): boolean {
    return !!this.config?.refreshToken;
  }

  // Simple one-click connection - no credentials needed
  connectDrive(): void {
    // Redirect to our simplified OAuth endpoint
    const currentOrigin = window.location.origin;
    const apiUrl = currentOrigin.includes('localhost')
      ? 'http://localhost:3000'
      : currentOrigin;

    window.location.href = `${apiUrl}/api/auth/oauth-google-drive?state=drive_backup`;
  }

  disconnect(): void {
    this.config = null;
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.encryptionKey);
    console.log('🔌 Google Drive disconnected');
  }

  async getValidAccessToken(): Promise<string> {
    if (!this.config) {
      throw new Error('Google Drive not configured');
    }

    // Check if token is expired (with 5 minute buffer)
    const now = new Date();
    const expiresWithBuffer = new Date(this.config.expiresAt.getTime() - 5 * 60 * 1000);

    if (now >= expiresWithBuffer) {
      await this.refreshAccessToken();
    }

    return this.config.accessToken;
  }

  private async refreshAccessToken(): Promise<void> {
    if (!this.config?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const currentOrigin = window.location.origin;
      const apiUrl = currentOrigin.includes('localhost')
        ? 'http://localhost:3000'
        : currentOrigin;

      const response = await fetch(`${apiUrl}/api/auth/refresh-google-drive-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: this.config.refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const { encodedTokens } = await response.json();
      await this.saveTokensFromUrl(encodedTokens);

      console.log('✅ Token refreshed successfully');
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Token refresh failed, user needs to reconnect
      this.disconnect();
      throw new Error('Please reconnect Google Drive');
    }
  }

  async uploadBackupFile(filename: string, content: string): Promise<string> {
    const accessToken = await this.getValidAccessToken();

    // Create file metadata
    const metadata = {
      name: filename,
      parents: ['appDataFolder'], // Store in app-specific folder (user's drive, not ours!)
      description: 'GodMode backup file'
    };

    const boundary = '-------314159265358979323846';
    const delimiter = "\r\n--" + boundary + "\r\n";
    const closeDelimiter = "\r\n--" + boundary + "--";

    // Create multipart body
    const multipartBody =
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: application/octet-stream\r\n\r\n' +
      content +
      closeDelimiter;

    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary="${boundary}"`
      },
      body: multipartBody
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Upload failed:', error);

      if (response.status === 401) {
        // Token might be invalid, try to refresh
        await this.refreshAccessToken();
        // Retry upload with new token
        return this.uploadBackupFile(filename, content);
      }

      throw new Error(`Failed to upload backup: ${response.status}`);
    }

    const result = await response.json();
    return result.id;
  }

  async listBackupFiles(): Promise<Array<{id: string; name: string; createdTime: string; size: string}>> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?` +
      `spaces=appDataFolder` +
      `&q=${encodeURIComponent("name contains 'godmode-backup'")}` +
      `&fields=files(id,name,createdTime,size)` +
      `&orderBy=createdTime desc` +
      `&pageSize=20`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        await this.refreshAccessToken();
        return this.listBackupFiles();
      }
      throw new Error(`Failed to list files: ${response.status}`);
    }

    const result = await response.json();
    return result.files || [];
  }

  async downloadBackupFile(fileId: string): Promise<string> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        await this.refreshAccessToken();
        return this.downloadBackupFile(fileId);
      }
      throw new Error(`Download failed: ${response.status}`);
    }

    return response.text();
  }

  async deleteBackupFile(fileId: string): Promise<void> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok && response.status !== 404) {
      if (response.status === 401) {
        await this.refreshAccessToken();
        return this.deleteBackupFile(fileId);
      }
      throw new Error(`Delete failed: ${response.status}`);
    }
  }

  // Get space usage info
  async getStorageInfo(): Promise<{ used: number; limit: number }> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(
      'https://www.googleapis.com/drive/v3/about?fields=storageQuota',
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      console.error('Failed to get storage info');
      return { used: 0, limit: 0 };
    }

    const data = await response.json();
    return {
      used: parseInt(data.storageQuota.usage || '0'),
      limit: parseInt(data.storageQuota.limit || '0')
    };
  }
}

export const simplifiedDriveService = new SimplifiedGoogleDriveService();